import type { AxiosResponse } from "axios";
import { NativeFilter } from "../modules/superset-embedded-sdk";
import { baseUrl } from "../config";
import axiosConfig from "./axiosConfig";

/** Dashboard title -> dataset column its month filter targets. */
const MONTH_FILTER_COLUMNS: ReadonlyMap<string, string> = new Map([
  ["Customer Financial Data", "month"],
  ["Employee Financial Data", "month"],
  ["Employee Financial Summary", "month"],
  ["Employee Project Finance Dashboard", "Month"],
  ["Employee Wise Project Allocation", "Month"],
  ["Employee Revenue and Utilization", "Month"],
  ["Employees Deployed By BU", "month"],
  ["Employee Deployed by Customer Group", "month"],
  ["FTE Dashboard", "Month"],
  ["FTE Rollup", "month_value"],
  ["FTE Standard Cost", "Month"],
  ["Ops Dashboard Rollup", "month_value"],
  ["Ops Dashboard Rollup Metrics", "Month"],
  ["Other Expense Costs Dashboard", "Month"],
  ["POD FTE Dashboard", "Month"],
  ["Procurement Costs Dashboard", "Month"],
  ["Project Financial Data", "month"],
  ["Employee Project Salary", "month"],
  ["SL Margin by Customer group", "Month"],
  ["SL Margin By Customer", "month_value"],
  ["SL Revenue By BU", "month_value"],
  ["SL Revenue By Employee", "Month"],
  ["SL Revenue By Project", "Month"],
  ["Sales Dashboard", "Month"],
  ["Space Timesheet Revenue", "Month"],
  ["Invoice List", "month"],
  ["Payroll List", "Month"],
  ["Timesheet Additional Expense", "Month"],
  ["Timesheet Calculated Revenue", "Month"],
  ["Timesheet Revenue By Customer Group", "Month"],
]);

type MonthFilterIdResponse = { id: string | null };

const monthFilterIdRequests = new Map<number, Promise<string | undefined>>();

async function requestMonthFilterId(
  dashboardId: number,
  column: string,
): Promise<string | undefined> {
  try {
    const response: AxiosResponse<MonthFilterIdResponse> = await axiosConfig({
      method: "get",
      url: `${baseUrl}/dashboard-native-filter-id/?dashboard_id=${dashboardId}&column=${encodeURIComponent(column)}`,
    });
    return response.data?.id ?? undefined;
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

/** One request per dashboard, shared by repeat calls. Misses are retried. */
function fetchMonthFilterId(
  dashboardId: number,
  column: string,
): Promise<string | undefined> {
  const cached = monthFilterIdRequests.get(dashboardId);
  if (cached) return cached;

  const request = requestMonthFilterId(dashboardId, column).then((filterId) => {
    if (!filterId) monthFilterIdRequests.delete(dashboardId);
    return filterId;
  });
  monthFilterIdRequests.set(dashboardId, request);
  return request;
}

/**
 * Default month filter for the dashboards in MONTH_FILTER_COLUMNS. The filter
 * id is read from the Superset dashboard metadata because it differs per
 * environment. The column is an integer (1-12), so the value is a number.
 */
export async function getEmbeddedNativeFilters(
  dashboardTitle: string,
  dashboardId: number,
  now: Date = new Date(),
): Promise<NativeFilter[] | undefined> {
  const column = MONTH_FILTER_COLUMNS.get(dashboardTitle.trim());
  if (!column) {
    return undefined;
  }

  const filterId = await fetchMonthFilterId(dashboardId, column);
  if (!filterId) {
    return undefined;
  }

  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getMonth() + 1;
  return [
    {
      id: filterId,
      column,
      operator: "IN",
      value: lastMonth,
    },
  ];
}
